import * as Dialog from "@radix-ui/react-dialog";
import { cn } from "../../lib/utils";
import type { ReactNode } from "react";

interface SheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
  title?: string;
}

export function Sheet({ open, onOpenChange, children, title }: SheetProps) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            "fixed inset-0 z-40 bg-black/40 backdrop-blur-[2px]",
            "data-[state=open]:animate-fade-in data-[state=closed]:animate-fade-out",
          )}
        />
        <Dialog.Content
          className={cn(
            "fixed right-0 top-0 z-50 h-full w-[420px] max-w-[90vw]",
            "bg-[var(--color-bg-sidebar)] border-l border-[var(--color-border-stroke)]",
            "flex flex-col overflow-hidden",
            "data-[state=open]:animate-slide-in-right data-[state=closed]:animate-slide-out-right",
            "focus-visible:outline-none",
          )}
        >
          {title && (
            <Dialog.Title className="sr-only">{title}</Dialog.Title>
          )}
          <Dialog.Description className="sr-only">
            {title ? `Details for ${title}` : "Detail panel"}
          </Dialog.Description>
          {children}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export function SheetHeader({ children, onClose }: { children: ReactNode; onClose: () => void }) {
  return (
    <div className="flex items-center justify-between px-[var(--spacing-4)] h-[var(--topbar-height)] border-b border-[var(--color-border-stroke)] shrink-0">
      <div className="flex items-center gap-[var(--spacing-2)] min-w-0 flex-1">
        {children}
      </div>
      <Dialog.Close asChild>
        <button
          onClick={onClose}
          className={cn(
            "rounded-[var(--radius-md)] p-[var(--spacing-1)] text-[var(--color-text-secondary)]",
            "hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-hover)]",
            "transition-colors duration-[var(--duration-fast)] cursor-pointer",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-focus)] focus-visible:ring-offset-2",
          )}
          aria-label="Close"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </button>
      </Dialog.Close>
    </div>
  );
}

export function SheetBody({ children }: { children: ReactNode }) {
  return (
    <div className="flex-1 overflow-y-auto p-[var(--spacing-4)]">
      {children}
    </div>
  );
}
